import type { DropdownProps } from '../../Dropdown';
import type { SupportedLanguages } from '@readme/iso/src/types/i18n';

import { SupportedLanguagesArray, LanguageDetails } from '@readme/iso/src/types/i18n';
import React, { useCallback, useState } from 'react';

import useClassy from '@core/hooks/useClassy';

import type { ButtonProps } from '@ui/Button';
import Button from '@ui/Button';
import Icon from '@ui/Icon';

import Dropdown from '../../Dropdown';
import Menu from '../../Menu';
import MenuItem from '../../Menu/Item';

import classes from './style.module.scss';

type LangPickerProps = Pick<
  ButtonProps,
  | 'circular'
  | 'disabled'
  | 'dropdown'
  | 'fullWidth'
  | 'ghost'
  | 'is'
  | 'kind'
  | 'link'
  | 'loading'
  | 'outline'
  | 'size'
  | 'target'
  | 'text'
  | 'to'
  | 'type'
  | 'uppercase'
> &
  Pick<DropdownProps, 'align' | 'justify' | 'open'> & {
    className?: string;
    /**
     * Array of language short codes to show in the picker. Defaults to all supported languages.
     */
    enabledLanguages?: `${SupportedLanguages}`[];
    /**
     * Choose between the globe, i18n, or translate glyphs.
     */
    glyph?: 'globe' | 'i18n' | 'translate';
    /**
     * Choose how to show or hide the icon.
     */
    icon?: 'first' | 'last' | 'none' | 'only';
    /**
     * Set the initial language selection by short code.
     */
    initialLanguage?: `${SupportedLanguages}`;
    /**
     * Show the full language name or just the short code.
     */
    label?: 'long' | 'native' | 'short';
    /**
     * Handler called with the selected short code when the user chooses a new language.
     */
    onChange?: (lang: `${SupportedLanguages}`) => void;
    style?: React.CSSProperties;
  };

const getLabel = (lang: string, labelType: 'long' | 'native' | 'short' = 'native') => {
  const shortcode = lang.toUpperCase();
  const labelKey = labelType === 'native' ? 'labelNative' : 'label';
  return LanguageDetails[shortcode]?.[labelKey] || shortcode;
};

export default function LangPicker({
  align = 'bottom',
  className,
  enabledLanguages = SupportedLanguagesArray,
  glyph = 'i18n',
  icon = 'first',
  initialLanguage = 'en',
  justify = 'start',
  label: labelType = 'long',
  onChange,
  open,
  style = {},
  ...buttonProps
}: LangPickerProps) {
  const bem = useClassy(classes, 'LangPicker');

  const [selected, setSelected] = useState(initialLanguage);

  const handleChange = useCallback(
    (lang: `${SupportedLanguages}`) => {
      setSelected(lang);
      onChange?.(lang);
    },
    [onChange],
  );

  return (
    <Dropdown
      align={align}
      className={`${bem()} ${className}`}
      clickInToClose={true}
      fullWidth={buttonProps.fullWidth}
      justify={justify}
      open={open}
      style={style}
      trigger="click"
    >
      <Button circular={icon === 'only'} uppercase={false} {...buttonProps} role="button">
        {['first', 'only'].includes(icon) && <Icon name={glyph} />}
        {icon !== 'only' && (labelType === 'short' ? selected.toUpperCase() : getLabel(selected, labelType))}
        {icon === 'last' && <Icon name={glyph} />}
      </Button>
      <Menu>
        {enabledLanguages.map?.(lang => (
          <MenuItem key={lang} active={selected === lang} onClick={() => handleChange(lang)}>
            {getLabel(lang, labelType)}
          </MenuItem>
        ))}
      </Menu>
    </Dropdown>
  );
}
